import {
  normalizeCode,
  type PlayerInfo,
  type ServerMessage,
  type SpawnAssign,
} from '../../shared/protocol';

export interface NetEvents {
  onOpen(): void;
  onWelcome(selfId: string, started: boolean, spawns: SpawnAssign, players: PlayerInfo[], stage: number): void;
  onPlayers(players: PlayerInfo[]): void;
  onStage(index: number): void;
  onStart(spawns: SpawnAssign, stage: number): void;
  onChat(id: string, name: string, text: string): void;
  onState(id: string, x: number, y: number, z: number, yaw: number, anim: string): void;
  onBye(id: string): void;
  onError(message: string): void;
  onClose(): void;
}

export function roomWsUrl(serverUrl: string, code: string, token?: string): string {
  const ws = serverUrl.replace(/^http/, 'ws').replace(/\/+$/, '');
  const path = `${ws}/api/room/${normalizeCode(code)}`;
  return token ? `${path}?token=${encodeURIComponent(token)}` : path;
}

/** Jeda reconnect ke-n (0-based): 1s, 2s, 4s, lalu 8s. Maks 6 percobaan. */
export const MAX_RECONNECT_ATTEMPTS = 6;
export function reconnectDelayMs(attempt: number): number {
  return [1000, 2000, 4000, 8000, 8000, 8000][Math.max(0, Math.min(5, attempt))];
}

type WsFactory = (url: string) => WebSocket;

/** Klien WebSocket room multiplayer (satu koneksi per game). */
export class NetClient {
  private ws: WebSocket | null = null;
  private events: NetEvents | null = null;
  private factory: WsFactory;

  constructor(factory?: WsFactory) {
    this.factory = factory ?? ((url) => new WebSocket(url));
  }

  static async createRoom(serverUrl: string, token?: string): Promise<string> {
    const body = token ? JSON.stringify({ token }) : undefined;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10_000);
    let res: Response;
    try {
      res = await fetch(`${serverUrl}/api/create`, {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body,
        signal: controller.signal,
      });
    } catch {
      throw new Error('terputus saat membuat room');
    } finally {
      window.clearTimeout(timer);
    }
    if (!res.ok) throw new Error(`create gagal (${res.status})`);
    const data = (await res.json()) as { code?: string };
    if (!data.code) throw new Error('server tak memberi kode');
    return data.code;
  }

  connect(serverUrl: string, code: string, events: NetEvents, token?: string): void {
    this.close();
    this.events = events;
    const ws = this.factory(roomWsUrl(serverUrl, code, token));
    this.ws = ws;
    ws.onopen = () => {
      this.events?.onOpen();
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      this.handle(msg);
    };
    ws.onclose = () => {
      this.ws = null;
      this.events?.onClose();
    };
    ws.onerror = () => {
      this.events?.onError('Koneksi bermasalah');
    };
  }

  get isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /** Ganti handler tanpa memutus koneksi (lobby -> game). */
  setEvents(events: NetEvents): void {
    this.events = events;
  }

  sendHello(name: string, color: string): void {
    this.send({ t: 'hello', name, color });
  }

  sendColor(color: string): void {
    this.send({ t: 'color', color });
  }

  sendReady(): void {
    this.send({ t: 'ready' });
  }

  sendChat(text: string): void {
    this.send({ t: 'chat', text });
  }

  sendStage(index: number): void {
    this.send({ t: 'stage', index });
  }

  sendState(x: number, y: number, z: number, yaw: number, anim: string): void {
    this.send({ t: 'state', x, y, z, yaw, anim });
  }

  close(): void {
    this.events = null;
    if (this.ws) {
      try {
        this.ws.onmessage = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.close();
      } catch {
        return;
      } finally {
        this.ws = null;
      }
    }
  }

  private handle(msg: ServerMessage): void {
    const ev = this.events;
    if (!ev) return;
    switch (msg.t) {
      case 'welcome':
        ev.onWelcome(msg.id, msg.started, msg.spawns, msg.players, msg.stage);
        ev.onPlayers(msg.players);
        break;
      case 'players':
        ev.onPlayers(msg.players);
        break;
      case 'stage':
        ev.onStage(msg.index);
        break;
      case 'chat':
        ev.onChat(msg.id, msg.name, msg.text);
        break;
      case 'start':
        ev.onStart(msg.spawns, msg.stage);
        break;
      case 'state':
        ev.onState(msg.id, msg.x, msg.y, msg.z, msg.yaw, msg.anim);
        break;
      case 'bye':
        ev.onBye(msg.id);
        break;
      case 'error':
        ev.onError(msg.message);
        break;
      default:
        break;
    }
  }

  private send(data: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(data));
      } catch {
        return;
      }
    }
  }
}
